import { SUPPORTED_STYLES, DEFAULT_STYLE, GUEST_STYLE } from '../platform/platformTypes.js';
import { normalizeCharacterStyle } from '../platform/styleNormalizer.js';

export class StyleSimulator {
  constructor(character, { onStyleChange }) {
    this.character = character;
    this.onStyleChange = onStyleChange || (() => {});
    this.activeStyle = { ...DEFAULT_STYLE };

    this.drawerEl = null;
    this.isOpen = false;
    this.init();
  }

  init() {
    // Only mount if development mode
    const isDev = typeof window !== 'undefined' && (
      window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1' ||
      import.meta.env?.DEV
    );

    if (!isDev) return;

    // Toggle button in bottom right
    const toggleBtn = document.createElement('button');
    toggleBtn.className = 'btn-secondary';
    toggleBtn.style.position = 'fixed';
    toggleBtn.style.bottom = '16px';
    toggleBtn.style.right = '16px';
    toggleBtn.style.zIndex = '100';
    toggleBtn.style.fontSize = '0.85rem';
    toggleBtn.style.padding = '8px 14px';
    toggleBtn.style.boxShadow = '0 4px 16px rgba(0, 0, 0, 0.4)';
    toggleBtn.style.pointerEvents = 'auto';
    toggleBtn.textContent = '🎨 Style Sim';
    toggleBtn.onclick = () => this.toggle();
    document.body.appendChild(toggleBtn);

    // Collapsible Drawer
    this.drawerEl = document.createElement('div');
    this.drawerEl.className = 'card-panel';
    this.drawerEl.style.position = 'fixed';
    this.drawerEl.style.top = '16px';
    this.drawerEl.style.right = '16px';
    this.drawerEl.style.width = '320px';
    this.drawerEl.style.maxHeight = 'calc(100vh - 80px)';
    this.drawerEl.style.overflowY = 'auto';
    this.drawerEl.style.zIndex = '99';
    this.drawerEl.style.display = 'none';
    this.drawerEl.style.pointerEvents = 'auto';
    this.drawerEl.style.fontSize = '0.85rem';

    // Drawer Header
    const header = document.createElement('div');
    header.style.display = 'flex';
    header.style.justifyContent = 'space-between';
    header.style.alignItems = 'center';
    header.style.marginBottom = '12px';

    const title = document.createElement('h3');
    title.textContent = 'Live Style Simulator';
    title.style.color = 'var(--color-primary)';
    title.style.fontSize = '1.05rem';
    header.appendChild(title);

    const close = document.createElement('button');
    close.className = 'btn-text';
    close.textContent = '✕';
    close.style.fontSize = '1rem';
    close.onclick = () => this.toggle(false);
    header.appendChild(close);
    this.drawerEl.appendChild(header);

    // Presets Row
    const presetsBox = document.createElement('div');
    presetsBox.style.marginBottom = '14px';
    const presetLabel = document.createElement('div');
    presetLabel.className = 'text-muted';
    presetLabel.style.marginBottom = '6px';
    presetLabel.textContent = 'QUICK PRESETS:';
    presetsBox.appendChild(presetLabel);

    const presetRow = document.createElement('div');
    presetRow.style.display = 'flex';
    presetRow.style.flexWrap = 'wrap';
    presetRow.style.gap = '6px';

    const presets = [
      {
        name: 'Afro Scarf',
        style: { body: 'female', hair: 'afro', hat: 'none', accessory: 'scarf', primaryColor: '#f05bd5', secondaryColor: '#1c2842', skinColor: '#8d5524', hairColor: '#17120f' }
      },
      {
        name: 'Crown Pony',
        style: { body: 'female', hair: 'ponytail', hat: 'crown', accessory: 'earrings', primaryColor: '#42e8e0', secondaryColor: '#131b2e', skinColor: '#d6a374', hairColor: '#e67e22' }
      },
      {
        name: 'Tophat Beard',
        style: { body: 'male', hair: 'short', hat: 'tophat', accessory: 'beard', primaryColor: '#2b2f4a', secondaryColor: '#0b1020', skinColor: '#f1c27d', hairColor: '#34495e' }
      },
      {
        name: 'Guest Look',
        style: GUEST_STYLE
      },
    ];

    for (const p of presets) {
      const pBtn = document.createElement('button');
      pBtn.className = 'btn-secondary';
      pBtn.style.padding = '4px 8px';
      pBtn.style.fontSize = '0.75rem';
      pBtn.textContent = p.name;
      pBtn.onclick = () => {
        this.activeStyle = { ...p.style };
        this.syncInputs();
        this.emitChange();
      };
      presetRow.appendChild(pBtn);
    }
    presetsBox.appendChild(presetRow);
    this.drawerEl.appendChild(presetsBox);

    // Form fields
    this.inputs = {};

    this.createSelectField('body', 'Body Silhouette', SUPPORTED_STYLES.BODIES);
    this.createSelectField('hair', 'Hairstyle', SUPPORTED_STYLES.HAIRSTYLES);
    this.createSelectField('hat', 'Hat / Headwear', SUPPORTED_STYLES.HATS);
    this.createSelectField('accessory', 'Accessory', SUPPORTED_STYLES.ACCESSORIES);

    this.createColorField('primaryColor', 'Primary Clothing');
    this.createColorField('secondaryColor', 'Secondary Clothing');
    this.createColorField('skinColor', 'Skin Tone');
    this.createColorField('hairColor', 'Hair Color');

    document.body.appendChild(this.drawerEl);
  }

  createSelectField(key, label, options) {
    const row = document.createElement('div');
    row.style.marginBottom = '10px';

    const lbl = document.createElement('label');
    lbl.className = 'text-secondary';
    lbl.style.display = 'block';
    lbl.style.marginBottom = '4px';
    lbl.textContent = label;
    row.appendChild(lbl);

    const select = document.createElement('select');
    select.style.width = '100%';
    select.style.padding = '6px';
    select.style.background = 'var(--color-surface-raised)';
    select.style.color = 'var(--color-text)';
    select.style.border = '1px solid var(--color-border)';
    select.style.borderRadius = 'var(--radius-control)';

    for (const opt of options) {
      const optEl = document.createElement('option');
      optEl.value = opt;
      optEl.textContent = opt;
      select.appendChild(optEl);
    }

    select.value = this.activeStyle[key];
    select.onchange = (e) => {
      this.activeStyle[key] = e.target.value;
      this.emitChange();
    };

    this.inputs[key] = select;
    row.appendChild(select);
    this.drawerEl.appendChild(row);
  }

  createColorField(key, label) {
    const row = document.createElement('div');
    row.style.marginBottom = '10px';
    row.style.display = 'flex';
    row.style.alignItems = 'center';
    row.style.justifyContent = 'space-between';

    const lbl = document.createElement('label');
    lbl.className = 'text-secondary';
    lbl.textContent = label;
    row.appendChild(lbl);

    const input = document.createElement('input');
    input.type = 'color';
    input.value = this.activeStyle[key];
    input.style.border = 'none';
    input.style.width = '36px';
    input.style.height = '28px';
    input.style.cursor = 'pointer';
    input.style.background = 'transparent';

    input.oninput = (e) => {
      this.activeStyle[key] = e.target.value;
      this.emitChange();
    };

    this.inputs[key] = input;
    row.appendChild(input);
    this.drawerEl.appendChild(row);
  }

  syncInputs() {
    for (const [key, el] of Object.entries(this.inputs)) {
      if (this.activeStyle[key]) {
        el.value = this.activeStyle[key];
      }
    }
  }

  toggle(forceState) {
    this.isOpen = typeof forceState === 'boolean' ? forceState : !this.isOpen;
    if (this.drawerEl) {
      this.drawerEl.style.display = this.isOpen ? 'block' : 'none';
    }
  }

  emitChange() {
    const normalized = normalizeCharacterStyle(this.activeStyle);
    if (this.character) {
      this.character.applyStyle(normalized);
    }
    this.onStyleChange(normalized);
  }
}
